import * as React from 'react';

import moment, { Moment } from 'moment';

import { rgba } from 'polished';
import styled from 'styled-components';

import { CalendarTiers, getPriceTier } from '@webapp/sdk/plans';

import DatepickerDayItem from './day-item';
import Typography from '@components/basics/typography';
import { useMemo } from 'react';

interface DatepickerCalendarProps {
  onChange?: (selected: Moment) => void;
  onRangeSelection?: (start: Moment, end?: Moment) => void;
  active: Moment;
  selected?: Moment;
  className?: any;
  min?: Moment;
  max?: Moment;
  isRangeSelector?: boolean;
  start?: Moment;
  end?: Moment;
  hasFilters?: boolean;
  hasChanges?: boolean;
  priceTiers?: CalendarTiers['priceTiers'];
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const DatepickerCalendar: React.FC<DatepickerCalendarProps> = (
  props
) => {
  const { active } = props;
  const isOtherMonth = (day: Moment) => !active.isSame(day, 'month');
  const isSelected = (day: Moment) => {
    if (props.isRangeSelector) {
      return !!(
        props.start?.isSame(day, 'day') ||
        props.end?.isSame(day, 'day') ||
        (props.start?.isBefore(day, 'day') && props.end?.isAfter(day, 'day'))
      );
    }

    return !!(props.selected && props.selected.isSame(day, 'date'));
  };

  const isRangeStart = (day: Moment) => !!props.start?.isSame(day, 'day');
  const isRangeEnd = (day: Moment) => {
    if (props.end) {
      return props.end.isSame(day, 'day');
    }
    return isRangeStart(day);
  };

  const isDisabled = (day: Moment) => {
    let disabled = false;

    if (props.min) {
      disabled = disabled || day.isBefore(props.min, 'date');
    }
    if (props.max) {
      disabled =
        disabled ||
        day.isSame(props.max, 'date') ||
        day.isAfter(props.max, 'date');
    }
    return disabled;
  };

  const handleRangeUpdate = (day: Moment) => {
    if (!props.onRangeSelection) {
      return;
    }

    if (
      !props.start ||
      day.isBefore(props.start) ||
      (props.start && props.end)
    ) {
      return props.onRangeSelection(day, undefined);
    }

    return props.onRangeSelection(props.start, day);
  };

  const selectDate = (
    ev: React.MouseEvent<HTMLTableDataCellElement, MouseEvent>,
    day: Moment
  ) => {
    if (!isDisabled(day)) {
      if (props.isRangeSelector) {
        ev.stopPropagation();
        handleRangeUpdate(day);
        return false;
      }

      if (props.onChange) {
        props.onChange(day);
        return false;
      }
    } else {
      ev.stopPropagation();
    }

    return false;
  };

  const calendarRows = useMemo((): Moment[][] => {
    const startOfMonth = moment(active).startOf('month');
    const firstDay = moment(startOfMonth).days(0);
    const endOfMonth = moment(active).endOf('month');
    const lastDay = moment(endOfMonth).days(6);

    const calendar: Moment[][] = [];
    let row: Moment[] = [firstDay];
    let pos = firstDay;

    do {
      pos = moment(pos).add(1, 'day');
      if (row.length === 7) {
        calendar.push(row);
        row = [];
      }
      row.push(pos);
    } while (!lastDay.isSame(pos, 'date'));

    calendar.push(row);

    return calendar;
  }, [active]);

  const isToday = (day: Moment) => {
    const today = moment().startOf('day');

    if (day.isSame(today, 'day')) {
      return true;
    }
  };

  const getPriceClassName = (day: Moment) => {
    if (
      (!props.min || day.isSameOrAfter(props.min)) &&
      (!props.max || day.isBefore(props.max))
    ) {
      const tier = getPriceTier(day, props.priceTiers || []);
      if (tier) {
        return tier.name;
      }
    }

    return 'free';
  };

  return (
    <table
      className={`${props.className} ${props.hasChanges ? 'has-changes' : ''}`}
    >
      <colgroup>
        <col className="day-column" />
      </colgroup>
      <thead>
        <tr className="head-days">
          {DAYS.map((day, idx) => (
            <th key={idx}>
              <Typography weight="700" uppercase>
                {day}
              </Typography>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {calendarRows?.map((row, i) => (
          <tr key={`row-${props.active.get('month')}-${i}`}>
            {row.map((day: Moment) => (
              <td
                key={`col-${Number(day)}`}
                onClick={(ev) => selectDate(ev, day)}
                className={`day-cell ${getPriceClassName(day)}`}
              >
                <Typography>
                  <DatepickerDayItem
                    inRange={props.isRangeSelector}
                    isRangeStart={isRangeStart(day)}
                    isRangeEnd={isRangeEnd(day)}
                    isSelected={isSelected(day)}
                    isDisabled={isDisabled(day)}
                    isOtherMonth={isOtherMonth(day)}
                    isToday={isToday(day)}
                    day={day}
                    hasChanges={props.hasChanges}
                  />
                </Typography>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default styled(DatepickerCalendar)`
  width: 100%;
  user-select: none;
  border-spacing: 0 5px;

  &.has-changes {
    & > * {
      span {
        color: ${({ theme }) => theme.colors.differentBackground};
      }
    }
  }

  .head-days {
    margin-top: 20px;
  }

  .day-cell {
    position: ${(props) => (props.isRangeSelector ? 'relative' : 'static')};
    width: calc(100% / 7);
    max-width: calc(100% / 7);
    height: 32px;
    max-height: 32px;
    padding: 0;
    z-index: 0;

    &.date-tier-1 {
      background-color: ${({ theme }) => rgba(theme.colors.blueColor, 0.65)};
    }

    &.date-tier-2 {
      background-color: ${({ theme }) => rgba(theme.colors.blueColor, 0.45)};
    }

    &.date-tier-3 {
      background-color: ${({ theme }) => rgba(theme.colors.blueColor, 0.25)};
    }
    &.date-tier-4 {
      background-color: ${({ theme }) => rgba(theme.colors.blueColor, 0.15)};
    }
    &.date-tier-5 {
      background-color: ${({ theme }) => rgba(theme.colors.blueColor, 0.06)};
    }
  }
`;
